import { Injectable } from "@angular/core";
import { CacheItem } from "./cache-item.model";
import { Observable, BehaviorSubject, of, throwError, defer } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { catchError, filter, map, shareReplay, tap } from "rxjs/operators";
import { LogService } from "../logging/log.service";

@Injectable({
  providedIn: "root",
})
export class CachingService {
  private cache = new Map<string, BehaviorSubject<CacheItem<any> | null>>();
  private inFlightRequests = new Map<string, Observable<CacheItem<any>>>();
  private defaultExpiryTime: number = 600000; // 10 minutes

  constructor(private http: HttpClient, private logger: LogService) {}

  private expired(lastUpdated: number, expiryTime: number): boolean {
    return Date.now() - lastUpdated > expiryTime;
  }

  /**
   * Returns an observable of data for the given cache key.
   * If the cached value is missing or expired (or if forceRefresh is true) and no request is in flight,
   * it issues an HTTP GET and updates the cache; otherwise, it returns the cached data.
   * If the inner HTTP requests fails, it returns the error on the shared outer subject.
   */
  getOrFetchData<T>(
    apiEndpoint: string,
    cacheKey: string,
    forceRefresh: boolean = false
  ): Observable<T> {
    return defer(() => {
      // Get (or create) the BehaviorSubject for this cache key.
      let subject = this.cache.get(
        cacheKey
      ) as BehaviorSubject<CacheItem<T> | null>;
      if (!subject) {
        subject = new BehaviorSubject<CacheItem<T> | null>(null);
        this.cache.set(cacheKey, subject);
      }

      const current = subject.getValue();
      // If a force refresh is requested, or there's no cached value or it is expired,
      // then trigger a fetch—if one is not already in flight.
      if (
        forceRefresh ||
        !current ||
        this.expired(current.timestamp, this.defaultExpiryTime)
      ) {
        if (!this.inFlightRequests.has(cacheKey)) {
          const request$ = this.http.get<T>(apiEndpoint).pipe(
            map(
              (data) => ({ value: data, timestamp: Date.now() } as CacheItem<T>)
            ),
            tap((cacheItem) => subject.next(cacheItem)),
            catchError((error) => {
              this.logger.error("Error fetching data:", error);
              subject.error(error);
              return throwError(() => error);
            }),
            shareReplay({ bufferSize: 1, refCount: true })
          );
          // Store the in-flight observable to prevent duplicate HTTP calls.
          this.inFlightRequests.set(cacheKey, request$);
          request$.subscribe({
            next: () => this.inFlightRequests.delete(cacheKey),
            error: () => this.inFlightRequests.delete(cacheKey),
          });
        }
      }
      return subject.asObservable();
    }).pipe(
      filter((item): item is CacheItem<T> => item !== null),
      map((item) => item.value),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  getData<T>(
    cacheKey: string,
    expiryTime: number = this.defaultExpiryTime
  ): Observable<T> {
    const subject = this.cache.get(cacheKey);
    if (subject) {
      return subject.asObservable().pipe(
        filter(
          (item): item is CacheItem<T> =>
            item !== null && !this.expired(item.timestamp, expiryTime)
        ),
        map((item) => item.value),
        shareReplay({ bufferSize: 1, refCount: true })
      );
    }
    return of(null);
  }

  setData<T>(cacheKey: string, cacheValue: T): void {
    const newItem: CacheItem<T> = { value: cacheValue, timestamp: Date.now() };
    let subject = this.cache.get(cacheKey) as
      | BehaviorSubject<CacheItem<T> | null>
      | undefined;
    if (subject) {
      subject.next(newItem);
    } else {
      subject = new BehaviorSubject<CacheItem<T> | null>(newItem);
      this.cache.set(cacheKey, subject);
    }
  }

  clearKey(key: string): void {
    this.cache.delete(key);
    this.inFlightRequests.delete(key);
  }

  clearAll(): void {
    this.cache.clear();
    this.inFlightRequests.clear();
  }
}
